// public/v3/src/lib/eligibility-infer.js
// QMB / dual-eligibility inference from cached Elation eligibility checks.
//
// Pure CommonJS so server routes and Jest tests can use the same logic.

const QMB_TEXT_RE = /\bQMB\b|Qualified\s+Medicare\s+Beneficiary/i;
const MSP_NON_QMB_RE = /Medicare\s+Secondary\s+Payer|\bSLMB\b|Specified\s+Low[-\s]?Income|\bQI[-\s]?[12]?\b(?![a-z])|Qualifying\s+Individual|\bQDWI\b|Qualified\s+Disabled/i;
const DISTRUST_LOOSE_QMB_SOURCES = ['elation-report-get', 'elation-policy-get'];
const FRESH_QMB_MS = 90 * 24 * 60 * 60 * 1000;

function readRaw(patient) {
  return (patient && patient._raw) || patient || {};
}

function eligibilityInsuranceId(ins) {
  if (!ins || typeof ins !== 'object') return '';
  return ins.id || ins.patientInsuranceId || ins.patient_insurance_id || ins.elationInsuranceId || '';
}

function isMedicareInsurance(ins) {
  if (!ins || ins.deleted_date) return false;
  const program = String(ins.paymentProgram || ins.payment_program || '');
  const carrier = String(ins.carrier || '');
  return /medicare/i.test(program) || /medicare/i.test(carrier);
}

function isMedicaidInsurance(ins) {
  if (!ins || ins.deleted_date) return false;
  const program = String(ins.paymentProgram || ins.payment_program || '');
  const carrier = String(ins.carrier || '');
  return /^medicaid$/i.test(program) || /medicaid/i.test(carrier);
}

function qmbCheckedAt(qmb) {
  if (!qmb || typeof qmb !== 'object') return '';
  return qmb.checkedAt || qmb.date || '';
}

function isManualQmb(qmb) {
  if (!qmb || typeof qmb !== 'object') return false;
  return qmb.source === 'manual' || (qmb.source == null && qmb.confirmed === true);
}

function isFreshQmb(qmb, nowMs = Date.now()) {
  if (!qmb || qmb.confirmed !== true) return false;
  // Manual confirmations expire too — QMB status changes month to month, so
  // an eternal operator attestation was an audit hole (G0558 kept flowing on
  // a years-old confirm). Honor an explicit expiresAt when present (the QMB
  // pill stamps checkedAt + 90d); otherwise the same 90-day window applies
  // to manual and cached confirmations alike. Undated confirms are stale.
  if (qmb.expiresAt) {
    const exp = new Date(qmb.expiresAt).getTime();
    if (Number.isFinite(exp)) return nowMs < exp;
  }
  const checkedAt = qmbCheckedAt(qmb);
  if (!checkedAt) return false;
  const ts = new Date(checkedAt).getTime();
  if (!Number.isFinite(ts)) return false;
  const age = nowMs - ts;
  return age >= 0 && age < FRESH_QMB_MS;
}

function checkInsuranceId(check) {
  if (!check || typeof check !== 'object') return '';
  return check.insuranceId || check.patientInsuranceId || check.patient_insurance_id || check.elationInsuranceId || '';
}

function checkTimestamp(check) {
  if (!check || typeof check !== 'object') return '';
  return check.ts || check.checkedAt || check.eligibility_check_timestamp || '';
}

function isFreshCheck(check, nowMs = Date.now()) {
  const ts = new Date(checkTimestamp(check)).getTime();
  if (!Number.isFinite(ts)) return false;
  const age = nowMs - ts;
  return age >= 0 && age < FRESH_QMB_MS;
}

function latestCheckForInsurance(checks, insuranceId) {
  let best = null;
  for (const c of checks) {
    if (!c || String(checkInsuranceId(c)) !== String(insuranceId)) continue;
    if (!best || String(checkTimestamp(c)) > String(checkTimestamp(best))) best = c;
  }
  return best;
}

function latestActiveCheck(checks, ids) {
  for (const id of ids) {
    const latest = latestCheckForInsurance(checks, id);
    if (latest && String(latest.status || '').toLowerCase() === 'active') return latest;
  }
  return null;
}

function hasQmbSignal(check) {
  if (!check || typeof check !== 'object') return false;
  if (QMB_TEXT_RE.test(check.insuranceType || '')) return true;
  if (check.qmbSignal !== true) return false;
  return check.strictQmb === true || !DISTRUST_LOOSE_QMB_SOURCES.includes(check.source);
}

function hasActiveQmbSignal(check) {
  if (!check || typeof check !== 'object') return false;
  if (!hasQmbSignal(check)) return false;
  const status = String(check.status || '').toLowerCase();
  return status === 'active' || check.qmbSignal === true;
}

function inferQmbStatus(patient) {
  const raw = readRaw(patient);
  const qmb = raw.qmb || null;
  const checks = Array.isArray(raw.eligibilityChecks) ? raw.eligibilityChecks : [];
  const insurances = Array.isArray(raw.insurances) ? raw.insurances : [];
  const medicareIds = insurances.filter(isMedicareInsurance).map(eligibilityInsuranceId).filter(Boolean);

  if (isManualQmb(qmb)) {
    if (!qmb.confirmed) {
      return {
        confirmed: false,
        source: 'manual',
        reason: 'operator cleared QMB',
        dualSignal: false,
      };
    }
    if (isFreshQmb(qmb)) {
      return {
        confirmed: true,
        source: 'manual',
        reason: 'operator confirmed via HETS/MAC',
        dualSignal: false,
      };
    }
    // Expired manual confirmation: fall through to the cached-check / dual
    // inference below. A stale attestation must not keep G0558 flowing, but
    // a newer Elation eligibility check can still confirm on its own.
  }

  const hasStrictCorroboration = (
    (qmb && qmb.strictQmb === true) ||
    QMB_TEXT_RE.test((qmb && qmb.insuranceType) || '') ||
    checks.some((check) => (
      medicareIds.some((id) => String(checkInsuranceId(check)) === String(id)) &&
      isFreshCheck(check) && (
        check.strictQmb === true || QMB_TEXT_RE.test(check.insuranceType || '')
      )
    ))
  );
  if (
    isFreshQmb(qmb) &&
    DISTRUST_LOOSE_QMB_SOURCES.includes(qmb.source) &&
    !hasStrictCorroboration
  ) {
    return {
      confirmed: false,
      source: qmb.source,
      reason: 'prior document-scan confirmation predates stricter QMB matching — re-run eligibility check',
      dualSignal: true,
    };
  }

  if (isFreshQmb(qmb)) {
    return {
      confirmed: true,
      source: qmb.source || 'cached',
      reason: 'fresh cached QMB confirmation',
      dualSignal: true,
    };
  }

  const medicaidIds = insurances.filter(isMedicaidInsurance).map(eligibilityInsuranceId).filter(Boolean);

  for (const id of medicareIds) {
    const latest = latestCheckForInsurance(checks, id);
    if (
      latest &&
      isFreshCheck(latest) &&
      hasActiveQmbSignal(latest)
    ) {
      const text = QMB_TEXT_RE.test(latest.insuranceType || '') ? `: "${latest.insuranceType}"` : ' report';
      return {
        confirmed: true,
        source: latest.source || 'elation-api-get',
        reason: `QMB text in Medicare eligibility${text}`,
        dualSignal: true,
      };
    }
  }

  const medicareActive = !!latestActiveCheck(checks, medicareIds);
  const medicaidActive = !!latestActiveCheck(checks, medicaidIds);
  const hasMspNonQmbSignal = checks.some((check) => (
    medicareIds.some((id) => String(checkInsuranceId(check)) === String(id)) &&
    isFreshCheck(check) &&
    check.strictQmb !== true &&
    !QMB_TEXT_RE.test(check.insuranceType || '') &&
    (check.mspSignal === true || MSP_NON_QMB_RE.test(check.insuranceType || ''))
  ));
  if (hasMspNonQmbSignal) {
    return {
      confirmed: false,
      source: 'inferred',
      reason: 'Medicare Savings/MSP text found but not QMB-specific — verify via HETS or Mark QMB',
      dualSignal: medicareActive && medicaidActive,
    };
  }
  if (medicareActive && medicaidActive) {
    return {
      confirmed: false,
      source: 'inferred',
      reason: 'Medicare + Medicaid both active; run new eligibility check to confirm QMB',
      dualSignal: true,
    };
  }

  return {
    confirmed: false,
    source: 'none',
    reason: checks.length ? 'no active QMB signal in cached checks' : 'no eligibility checks on file',
    dualSignal: false,
  };
}

module.exports = {
  DISTRUST_LOOSE_QMB_SOURCES,
  FRESH_QMB_MS,
  MSP_NON_QMB_RE,
  QMB_TEXT_RE,
  checkInsuranceId,
  eligibilityInsuranceId,
  hasActiveQmbSignal,
  inferQmbStatus,
  hasQmbSignal,
  isFreshQmb,
  isFreshCheck,
  isManualQmb,
  isMedicaidInsurance,
  isMedicareInsurance,
  latestCheckForInsurance,
  qmbCheckedAt,
};
